// Campana de alertas. En todas las vistas abre un desplegable no modal con los últimos alertas; en
// móvil el panel ocupa el ancho de la pantalla bajo la barra superior. "Ver todos" lleva a
// /notifications (bandeja completa y ajustes de push).
import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { BellIcon, CheckAllIcon, ChevronRightIcon } from '../../ui/icons.tsx';
import { InboxList } from './inbox.tsx';
import { useInbox } from './use-inbox.ts';

const PREVIEW_LIMIT = 8;

function bellLabel(count: number) {
  return count > 0 ? `Alertas: ${count} sin leer` : 'Alertas';
}

function BellGlyph({ count }: { count: number }) {
  return (
    <>
      <BellIcon />
      {count > 0 ? (
        <span className="bell-count" aria-hidden="true">
          {count > 99 ? '99+' : count}
        </span>
      ) : null}
    </>
  );
}

export function NotificationBell({ count }: { count: number }) {
  const panelId = useId();
  const titleId = useId();
  // Abierto solo en la ruta donde se abrió: al navegar (abrir un aviso) se cierra solo.
  const location = useLocation();
  const [openAt, setOpenAt] = useState<string | null>(null);
  const open = openAt === location.pathname;
  const setOpen = (value: boolean) => setOpenAt(value ? location.pathname : null);
  const wrapper = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const { inbox, unread, open: openItem, markAll } = useInbox(open);

  useEffect(() => {
    if (!open) return;
    panel.current?.focus();
    const onPointer = (event: PointerEvent) => {
      if (wrapper.current && !wrapper.current.contains(event.target as Node)) setOpenAt(null);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenAt(null);
        button.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="bell-wrap" ref={wrapper}>
      <button
        ref={button}
        type="button"
        className={`bell${open ? ' bell-open' : ''}`}
        aria-label={bellLabel(count)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen(!open)}
      >
        <BellGlyph count={count} />
      </button>
      {open ? (
        <div ref={panel} id={panelId} className="popover" role="dialog" aria-labelledby={titleId} tabIndex={-1}>
          <div className="popover-header">
            <h2 id={titleId}>Alertas</h2>
            {unread > 0 ? (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => void markAll()}>
                <CheckAllIcon />
                Marcar todos como leídos
              </button>
            ) : null}
          </div>
          <div className="popover-body">
            <InboxList inbox={inbox} onOpen={(item) => void openItem(item)} limit={PREVIEW_LIMIT} />
          </div>
          <Link to="/notifications" className="popover-footer" onClick={() => setOpen(false)}>
            Ver todas las alertas y ajustes
            <ChevronRightIcon />
          </Link>
        </div>
      ) : null}
    </div>
  );
}
